import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PwaInstallPromptComponent } from './components/pwa-install-prompt/pwa-install-prompt.component';
import { AuthService } from './services/auth.service';
import { EntertainmentService } from './services/entertainment.service';
import { SnakeGameComponent } from './components/snake-game/snake-game.component';
import { MemoryGameComponent } from './components/memory-game/memory-game.component';

interface SubMenuItem {
  id: string;
  icon: string;
  label: string;
  route?: string;
  queryParams?: Record<string, string>;
  action?: string;
  disabled?: boolean;
  disabledHint?: string;
}

interface NavTab {
  id: string;
  label: string;
  icon?: string;
  logoSrc?: string;
  route?: string;
  submenu?: SubMenuItem[];
  disabled?: boolean;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule, RouterOutlet, RouterModule, PwaInstallPromptComponent,
    SnakeGameComponent, MemoryGameComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'barclik-app';
  isAuthenticated = false;
  activeSubmenu: string | null = null;
  isDarkMode = false;

  private authSub?: Subscription;
  private routerSub?: Subscription;

  @ViewChild('snakeGame') snakeGame!: SnakeGameComponent;
  @ViewChild('memoryGame') memoryGame!: MemoryGameComponent;

  navTabs: NavTab[] = [
    {
      id: 'inicio',
      label: 'Inicio',
      logoSrc: 'assets/logo.png',
      route: '/dashboard'
    },
    {
      id: 'lugares',
      label: 'Lugares',
      disabled: true,
      submenu: [
        { id: 'cercanos', icon: '📍', label: 'Cercanos', route: '/nearby-restaurants', disabled: true },
        { id: 'buscar', icon: '🔍', label: 'Buscar', route: '/buscar', disabled: true },
        { id: 'chat', icon: '💬', label: 'Chat', route: '/chat', disabled: true },
      ]
    },
    {
      id: 'pedidos',
      label: 'Pedidos',
      submenu: [
        { id: 'buscar', icon: '🔍', label: 'Buscar', route: '/buscar' },
        { id: 'mis-pedidos', icon: '📦', label: 'Historial', route: '/order-history' },
        { id: 'mis-reservas', icon: '📅', label: 'Reservas', route: '/order-history', queryParams: { tab: 'reservations' } },
        { id: 'chat', icon: '💬', label: 'Chat', route: '/chat', disabled: true, disabledHint: 'Proximamente' },
      ]
    },
    {
      id: 'juegos',
      label: 'Juegos',
      submenu: [
        { id: 'snake', icon: '🐍', label: 'Snake', action: 'openSnakeGame' },
        { id: 'memory', icon: '🧠', label: 'Parejas', action: 'openMemoryGame' },
      ]
    },
    {
      id: 'perfil',
      label: 'Perfil',
      submenu: [
        { id: 'datos', icon: '👤', label: 'Datos', route: '/user-profile', queryParams: { section: 'datos' } },
        { id: 'nivel', icon: '🎯', label: 'Reputacion', route: '/user-profile', queryParams: { section: 'nivel' } },
        { id: 'verificar', icon: '🔐', label: 'Verificar', route: '/user-profile', queryParams: { section: 'verificar' } },
        { id: 'ajustes', icon: '⚙️', label: 'Ajustes', route: '/user-profile', queryParams: { section: 'ajustes' } },
        { id: 'app', icon: '📱', label: 'App', route: '/user-profile', queryParams: { section: 'app' } },
      ]
    },
  ];

  constructor(
    private authService: AuthService,
    private entertainmentService: EntertainmentService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadThemePreference();

    this.authSub = this.authService.isAuthenticated$.subscribe(auth => {
      this.isAuthenticated = auth;
      if (!auth) this.closeSubmenu();
    });

    this.routerSub = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => {
      this.closeSubmenu();
    });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  onTabClick(tab: NavTab): void {
    if (tab.disabled) return;
    if (tab.route && !tab.submenu) {
      this.closeSubmenu();
      this.router.navigate([tab.route]);
    } else if (tab.submenu) {
      this.toggleSubmenu(tab.id);
    }
  }

  toggleSubmenu(tabId: string): void {
    if (this.activeSubmenu === tabId) {
      this.closeSubmenu();
    } else {
      this.activeSubmenu = tabId;
    }
  }

  closeSubmenu(): void {
    this.activeSubmenu = null;
  }

  onSubmenuItemClick(item: SubMenuItem): void {
    if (item.disabled) return;
    if (item.action) {
      this.executeAction(item.action);
    }
    this.closeSubmenu();
    if (item.route) {
      this.router.navigate([item.route], { queryParams: item.queryParams });
    }
  }

  getActiveSubmenuItems(): SubMenuItem[] {
    const tab = this.navTabs.find(t => t.id === this.activeSubmenu);
    return tab?.submenu || [];
  }

  isTabActive(tab: NavTab): boolean {
    if (tab.id === this.activeSubmenu) return true;
    if (tab.route) {
      return this.router.url.startsWith(tab.route);
    }
    return false;
  }

  toggleTheme(): void {
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme();
    localStorage.setItem('barclik-theme', this.isDarkMode ? 'dark' : 'light');
  }

  private loadThemePreference(): void {
    const saved = localStorage.getItem('barclik-theme');
    if (saved) {
      this.isDarkMode = saved === 'dark';
    } else {
      this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    this.applyTheme();
  }

  private applyTheme(): void {
    if (this.isDarkMode) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }

  private executeAction(action: string): void {
    switch (action) {
      case 'openSnakeGame': this.snakeGame?.openGame(); break;
      case 'openMemoryGame': this.memoryGame?.openGame(); break;
    }
  }
}
